/* eslint-disable max-params, no-mixed-operators */
import { Vector3 } from 'three';

/**
 * Interpolate a position along an array of points
 * @param path Array of Vector3 points
 * @param progress Normalized progress (0 to 1)
 * @returns Interpolated Vector3 position
 */
export const interpolateAlongPath = (path: Vector3[], progress: number): Vector3 => {
	if (path.length === 0) {
		return new Vector3();
	}
	if (path.length === 1) {
		return path[0].clone();
	}

	const clampedProgress = Math.max(0, Math.min(1, progress));
	const numPoints = path.length;

	// Map progress to index
	const indexFloat = clampedProgress * (numPoints - 1);
	const lowerIndex = Math.floor(indexFloat);
	const upperIndex = Math.min(lowerIndex + 1, numPoints - 1);
	const localT = indexFloat - lowerIndex;

	// Interpolate between two nearest points
	const result = new Vector3();
	result.lerpVectors(path[lowerIndex], path[upperIndex], localT);
	return result;
};

/**
 * Linear interpolation between two vectors
 * @param start Start position
 * @param end End position
 * @param progress Normalized progress (0 to 1)
 * @returns Interpolated Vector3 position
 */
export const lerpVector3 = (start: Vector3, end: Vector3, progress: number): Vector3 => {
	const result = new Vector3();
	result.lerpVectors(start, end, Math.max(0, Math.min(1, progress)));
	return result;
};

/**
 * Generate points along a quadratic bezier curve
 * @param start Start point
 * @param control Control point (the curve bends toward this)
 * @param end End point
 * @param segments Number of segments to generate
 * @returns Array of Vector3 points along the curve
 */
export const generateQuadraticBezierPoints = (
	start: Vector3,
	control: Vector3,
	end: Vector3,
	segments = 32
): Vector3[] => {
	const points: Vector3[] = [];

	for (let i = 0; i <= segments; i++) {
		const t = i / segments;
		const oneMinusT = 1 - t;

		// Quadratic bezier: (1-t)²P0 + 2(1-t)tP1 + t²P2
		const x = oneMinusT * oneMinusT * start.x + 2 * oneMinusT * t * control.x + t * t * end.x;
		const y = oneMinusT * oneMinusT * start.y + 2 * oneMinusT * t * control.y + t * t * end.y;
		const z = oneMinusT * oneMinusT * start.z + 2 * oneMinusT * t * control.z + t * t * end.z;

		points.push(new Vector3(x, y, z));
	}

	return points;
};

/**
 * Calculate total path length
 * @param path Array of Vector3 points
 * @returns Total length of the path
 */
export const calculatePathLength = (path: Vector3[]): number => {
	if (path.length < 2) return 0;

	let length = 0;
	for (let i = 1; i < path.length; i++) {
		length += path[i].distanceTo(path[i - 1]);
	}
	return length;
};

